export const INTEGRATED_LEARNING_SCHEMA_VERSION = "forfeo-lot4i-i1-v1" as const;

export const LEARNING_PROFILE_CATALOG = {
  merchant: {
    label: { fr: "Marchand", en: "Merchant" },
    primaryRepository: "GIFTSHOP",
    surfaceHints: ["merchant", "commerce", "boutique", "order", "voucher"],
  },
  prp: {
    label: { fr: "Président régional partenaire", en: "Regional Partner President" },
    primaryRepository: "GIFTSHOP",
    surfaceHints: ["prp", "regional", "partner", "affiliate", "commission"],
  },
  enterprise: {
    label: { fr: "Entreprise", en: "Corporate" },
    primaryRepository: "GIFTSHOP",
    surfaceHints: ["corporate", "enterprise", "gift", "batch", "invoice"],
  },
  landlord: {
    label: { fr: "Propriétaire", en: "Landlord" },
    primaryRepository: "EXPLORE",
    surfaceHints: ["landlord", "owner", "propriétaire", "habitat", "lease"],
  },
  tenant: {
    label: { fr: "Locataire", en: "Tenant" },
    primaryRepository: "EXPLORE",
    surfaceHints: ["tenant", "locataire", "habitat", "rent", "lease"],
  },
  administrator: {
    label: { fr: "Administrateur", en: "Administrator" },
    primaryRepository: "EXPLORE",
    surfaceHints: ["admin", "operations", "governance", "moderation", "platform"],
  },
  support: {
    label: { fr: "Soutien", en: "Support" },
    primaryRepository: "EXPLORE",
    surfaceHints: ["support", "help", "assistance", "case", "incident"],
  },
} as const;

export type LearningProfile = keyof typeof LEARNING_PROFILE_CATALOG;

export const LEARNING_FORMATS = {
  pdfJobAid: {
    label: { fr: "Aide de travail PDF", en: "PDF job aid" },
    downloadable: true,
    interactive: false,
  },
  stepByStepGuide: {
    label: { fr: "Guide étape par étape", en: "Step-by-step guide" },
    downloadable: false,
    interactive: false,
  },
  interactiveTutorial: {
    label: { fr: "Tutoriel interactif", en: "Interactive tutorial" },
    downloadable: false,
    interactive: true,
  },
  presentation: {
    label: { fr: "Présentation de formation", en: "Training presentation" },
    downloadable: true,
    interactive: false,
  },
  immersiveDemo3d: {
    label: { fr: "Démonstration immersive 3D", en: "Immersive 3D demonstration" },
    downloadable: false,
    interactive: true,
  },
} as const;

export type LearningFormat = keyof typeof LEARNING_FORMATS;

export const LEARNING_PATHS = [
  "getting_started",
  "daily_operations",
  "exceptions_and_recovery",
  "security_and_privacy",
  "support_and_escalation",
] as const;

export const INTEGRATED_LEARNING_GOVERNANCE = {
  locales: ["fr", "en"] as const,
  contentAuthority: "EXPLORE",
  financialAuthority: "GIFTSHOP",
  browserFinancialMutationAllowed: false,
  productionDataAllowedInTrainingAssets: false,
  secretsAllowedInTrainingAssets: false,
  publicationRequiresReview: true,
  versionRequired: true,
  lastReviewedAtRequired: true,
  contextualRouteRequired: true,
  accessibleTextAlternativeRequired: true,
  keyboardPathRequired: true,
  captionsRequiredForNarratedMedia: true,
  reducedMotionRequired: true,
  mobileLayoutRequired: true,
  immersive3dIsOptionalEnhancement: true,
  immersive3dFallbackRequired: true,
} as const;

export type LocalizedText = { fr: string; en: string };

export type LearningAssetCandidate = {
  profile: LearningProfile;
  format: LearningFormat;
  title: LocalizedText;
  summary: LocalizedText;
  owner: string | null;
  reviewer: string | null;
  version: string | null;
  lastReviewedAt: string | null;
  contextualRoute: string | null;
  accessibleTextAlternative: string | null;
  keyboardPathDocumented: boolean;
  reducedMotionSupported: boolean;
  mobileLayoutSupported: boolean;
  immersiveFallbackRoute: string | null;
  usesProductionData: boolean;
  containsSecrets: boolean;
};

const present = (value: string | null | undefined) => Boolean(value?.trim());

export function evaluateLearningAssetReadiness(candidate: LearningAssetCandidate) {
  const missingLocales = INTEGRATED_LEARNING_GOVERNANCE.locales.filter((locale) => (
    !present(candidate.title[locale]) || !present(candidate.summary[locale])
  ));
  const governanceReady = present(candidate.owner)
    && present(candidate.reviewer)
    && candidate.owner !== candidate.reviewer
    && present(candidate.version)
    && present(candidate.lastReviewedAt)
    && present(candidate.contextualRoute);
  const accessibilityReady = present(candidate.accessibleTextAlternative)
    && candidate.keyboardPathDocumented
    && candidate.reducedMotionSupported
    && candidate.mobileLayoutSupported;
  const immersiveFallbackReady = candidate.format !== "immersiveDemo3d"
    || present(candidate.immersiveFallbackRoute);
  const privacyReady = !candidate.usesProductionData && !candidate.containsSecrets;

  return {
    ready: missingLocales.length === 0
      && Boolean(governanceReady)
      && Boolean(accessibilityReady)
      && Boolean(immersiveFallbackReady)
      && privacyReady,
    missingLocales,
    governanceReady: Boolean(governanceReady),
    accessibilityReady: Boolean(accessibilityReady),
    immersiveFallbackReady: Boolean(immersiveFallbackReady),
    privacyReady,
  } as const;
}
